import { Badge, Button, Card, CardBody, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Progress, TabContent, TabPane } from "reactstrap";
import './styles.css';
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { useTranslation } from "react-i18next";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { useEffect, useRef, useState } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import OverviewTab from "./Tabs/Overview";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { del, get, post, postForm } from "../../../../utils/helpers/api";
import Timeline from "../../components/Timeline";
import { LiaCameraSolid } from "react-icons/lia";
import {
  IoAlertCircleOutline, IoAttachOutline, IoCalendarOutline, IoCallOutline, IoCashOutline,
  IoCopyOutline, IoCreateOutline, IoDocumentTextOutline, IoGlobeOutline, IoLocationOutline,
  IoMailOutline, IoPersonOutline, IoPricetagsOutline, IoReaderOutline, IoReceiptOutline,
  IoTrashOutline, IoWalletOutline
} from "react-icons/io5";
import { AWS_S3_URL } from "../../../../utils/constants";
import { useSettings } from "../../../../context/settings";
import { formatMoney, useCurrency } from "../../../../utils/helpers/currency";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { toast } from "react-toastify";
import {
  Avatar, Dot, Fact, initialsOf, RecordLayout, RecordMain, RecordNav, RecordRail, RecordTabs,
  StatStrip, useScrollSpy
} from "../../components/Record";

function ViewCustomerPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const currency = useCurrency();
  const NAV_SECTIONS = [
    { id: 'general-information-card', label: t('crm.generalInformation'), icon: <IoPersonOutline size={14} /> },
    { id: 'contact-information-card', label: t('crm.contactInformationsSection'), icon: <IoCallOutline size={14} /> },
    { id: 'address-card', label: t('common.address'), icon: <IoLocationOutline size={14} /> },
    { id: 'notes-card', label: t('common.notes'), icon: <IoReaderOutline size={14} /> },
    { id: 'attachments-card', label: t('attachments.title'), icon: <IoAttachOutline size={14} /> },
    { id: 'invoices-card', label: t('sales.invoices'), icon: <IoReceiptOutline size={14} /> },
    { id: 'quotes-card', label: t('sales.quotes.title'), icon: <IoDocumentTextOutline size={14} /> },
    { id: 'payments-card', label: t('sales.payments'), icon: <IoCashOutline size={14} /> },
    { id: 'meetings-card', label: t('crm.meetings'), icon: <IoCalendarOutline size={14} /> }
  ];
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [customerData, setCustomerData] = useState(state?.customerData || {});
  const [financials, setFinancials] = useState({});
  const [profilePicture, setProfilePicture] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const { activeSection, scrollToSection } = useScrollSpy(
    NAV_SECTIONS.map(section => section.id),
    scrollContainerRef,
    activeTab === 'overview',
    customerData?.id
  );

  useEffect(() => {
    if (id) {
      getCustomer();
    }
  }, [id]);

  const getCustomer = async () => {
    const data = await get('/crm/customers/view/' + id);
    if (data?.data) {
      setCustomerData(data.data);
    }
    if (data?.financials) {
      setFinancials(data.financials);
    }
  };

  const cloneCustomer = async () => {
    const data = await post('/crm/customers/clone/' + id);
    if (data?.customer) {
      navigate('/crm/customers/edit/' + data.customer.id, { state: { customerData: data.customer } });
    }
  };

  const deleteCustomer = async () => {
    const data = await del('/crm/customers/delete/' + id);

    if (data?.success) {
      toast.success(data.message || t('crm.customerDeleted'));
      navigate('/crm/customers');
    }
  };

  const handleProfilePictureChange = async (file) => {
    const data = new FormData();
    data.append('profile_picture', file);

    Promise.resolve(postForm(`/crm/customers/upload-profile-picture/${id}`, data))
      .then(() => {
        getCustomer();
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (profilePicture) {
      handleProfilePictureChange(profilePicture);
    }
  }, [profilePicture]);

  const customerName = [customerData.firstname, customerData.lastname].filter(Boolean).join(' ')
    || customerData.company_name
    || t('crm.customer');

  const pickProfilePicture = () => fileInputRef.current.click();

  const avatar = (
    <>
      {customerData?.profile_picture_path ? (
        <img
          src={AWS_S3_URL + customerData.profile_picture_path}
          alt={t('crm.profileAlt')}
          className="subheader-avatar"
          onClick={pickProfilePicture}
        />
      ) : (
        <div className="subheader-avatar-placeholder" onClick={pickProfilePicture}>
          <LiaCameraSolid color="#adb5bd" size={15} />
        </div>
      )}
      <input
        type="file"
        className="d-none"
        ref={fileInputRef}
        onChange={(e) => setProfilePicture(e.target.files[0])}
      />
    </>
  );

  const money = (value) => `${formatMoney(value)} ${currency}`;

  const invoiced = Number(financials.total_invoiced ?? 0);
  const paid = Number(financials.total_paid ?? 0);
  const outstanding = Number(financials.outstanding_balance ?? 0);
  const collectedPercent = invoiced > 0 ? Math.min(100, Math.round((paid / invoiced) * 100)) : 0;

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          title={customerName}
          onBack={() => navigate('/crm/customers')}
          avatar={avatar}
          onReload={getCustomer}
        >
          <Button
            size="sm"
            outline
            color="secondary"
            className="d-flex align-items-center gap-1"
            onClick={() => navigate("/crm/customers/edit/" + id, { state: { customerData } })}
          >
            <IoCreateOutline size={14} />
            {t('common.edit')}
          </Button>
          <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down" size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => navigate("/sales/quotes/create?customer_id=" + id)}>
                <IoDocumentTextOutline size={15} /> {t('sales.newQuote')}
              </DropdownItem>
              <DropdownItem onClick={() => navigate("/sales/invoices/create?customer_id=" + id)}>
                <IoDocumentTextOutline size={15} /> {t('sales.newInvoice')}
              </DropdownItem>
              <DropdownItem onClick={cloneCustomer}>
                <IoCopyOutline size={15} /> {t('common.clone')}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger" onClick={() => setDeleteOpen(true)}>
                <IoTrashOutline size={15} /> {t('common.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content scrollable" ref={scrollContainerRef}>
          <RecordLayout>
            <RecordRail>
              <Card>
                <CardBody className="text-center pb-3">
                  <Avatar
                    src={customerData?.profile_picture_path ? AWS_S3_URL + customerData.profile_picture_path : null}
                    initials={initialsOf(customerName)}
                    fallback={<LiaCameraSolid size={22} />}
                    onClick={pickProfilePicture}
                  />

                  <h6 className="fw-bold mb-1 mt-3">{customerName}</h6>
                  {customerData?.company_name && (
                    <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                      {customerData.company_name}
                    </div>
                  )}
                  {customerData?.type && (
                    <Badge color={customerData.type === 'company' ? 'info' : 'secondary'}>
                      {t('crm.' + customerData.type)}
                    </Badge>
                  )}

                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <Button
                      size="sm"
                      outline
                      color="secondary"
                      disabled={!customerData?.primary_email}
                      onClick={() => { window.location.href = `mailto:${customerData.primary_email}`; }}
                    >
                      <IoMailOutline size={14} />
                    </Button>
                    <Button
                      size="sm"
                      outline
                      color="secondary"
                      disabled={!customerData?.primary_phone}
                      onClick={() => { window.location.href = `tel:${customerData.primary_phone}`; }}
                    >
                      <IoCallOutline size={14} />
                    </Button>
                  </div>
                </CardBody>

                <hr className="my-0" />

                <CardBody className="d-flex flex-column gap-2">
                  <Fact
                    icon={<IoMailOutline size={14} />}
                    value={customerData?.primary_email}
                    href={`mailto:${customerData?.primary_email}`}
                  />
                  <Fact
                    icon={<IoCallOutline size={14} />}
                    value={customerData?.primary_phone}
                    href={`tel:${customerData?.primary_phone}`}
                  />
                  <Fact icon={<IoGlobeOutline size={14} />} value={customerData?.country} />
                  <Fact icon={<IoPricetagsOutline size={14} />} value={customerData?.tax_id} />
                  <Fact
                    icon={<IoCalendarOutline size={14} />}
                    value={customerData?.created_at ? `${t('crm.customerSince')} ${formatDate(customerData.created_at)}` : null}
                  />
                </CardBody>
              </Card>

              {activeTab === 'overview' && (
                <RecordNav sections={NAV_SECTIONS} activeId={activeSection} onSelect={scrollToSection} />
              )}
            </RecordRail>

            <RecordMain>
              <StatStrip
                tiles={[
                  {
                    icon: <IoReceiptOutline size={18} />,
                    background: '#f0eeff',
                    color: '#6C63FF',
                    value: money(invoiced),
                    label: t('crm.totalInvoiced'),
                    count: financials.invoices_count
                  },
                  {
                    icon: <IoCashOutline size={18} />,
                    background: '#e9f9f0',
                    color: '#22a06b',
                    value: money(paid),
                    label: t('common.totalPaid'),
                    count: financials.payments_count
                  },
                  {
                    icon: <IoWalletOutline size={18} />,
                    background: '#fff3e6',
                    color: '#fd7e14',
                    value: money(outstanding),
                    label: t('crm.outstandingBalance')
                  }
                ]}
              >
              </StatStrip>

              <RecordTabs
                tabs={[
                  { id: 'overview', label: t('crm.overview') },
                  { id: 'timeline', label: t('crm.timeline') }
                ]}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
              <TabContent activeTab={activeTab} className="border-0 p-0">
                <TabPane tabId="overview">
                  <OverviewTab customerData={customerData} financials={financials} />
                </TabPane>
                <TabPane tabId="timeline">
                  <Timeline subjectType="customer" subjectId={id} />
                </TabPane>
              </TabContent>
            </RecordMain>
          </RecordLayout>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteOpen}
        title={t('crm.deleteCustomer')}
        message={t('crm.deleteCustomerConfirm')}
        onConfirm={deleteCustomer}
        toggle={() => setDeleteOpen(false)}
      />
    </SidebarLayout>
  );
}

export default ViewCustomerPage;
